import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Box,
  LinearProgress,
  LinearProgressProps,
  Stack,
  Typography
} from '@mui/material'
import { ReactElement, useEffect, useMemo, useState } from 'react'

import { SectionWithRelated } from '../../../models/inspections'
import {
  checkSingleEntryIsCompleteOrNotRequired,
  getNumOfCompletedEntriesInSection,
  getNumOfCompletedRequiredEntriesInSection,
  getNumOfRequiredEntriesInSection
} from '../../../utils/checkInspectionEntryRequiredNotesOrPhoto'
import { SinglePromptOfEntry } from './SinglePromptOfEntry'
import { SinglePromptOfEntryCached } from './SinglePromptOfEntryCached'

type Props = {
  section: SectionWithRelated
  showIncomplete: boolean
  isCached?: boolean
} & Omit<AccordionProps, 'children'>
function LinearProgressWithLabel(
  props: LinearProgressProps & {
    numOfCompletedEntries: number
    numOfTotalEntries: number
  }
) {
  const { numOfCompletedEntries, numOfTotalEntries, ...defaultProps } = props
  const text = `${numOfCompletedEntries}/${numOfTotalEntries}`

  return (
    <Stack direction="row" alignItems={'center'} spacing={1}>
      <Box sx={{ width: '80px' }}>
        <LinearProgress
          variant="determinate"
          {...defaultProps}
          color={
            numOfTotalEntries === numOfCompletedEntries ? 'success' : 'warning'
          }
          value={
            numOfTotalEntries === 0
              ? 100
              : (numOfCompletedEntries / numOfTotalEntries) * 100
          }
        />
      </Box>
      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>
    </Stack>
  )
}

function SingleSection({
  section,
  showIncomplete,
  isCached,
  ...accordionProps
}: Props): ReactElement {
  const [expanded, setExpanded] = useState(false)
  const numOfTotalEntries = section.entries.length
  const numOfCompletedEntries = useMemo(
    () => getNumOfCompletedEntriesInSection(section),
    [section]
  )
  const numOfRequiredEntries = useMemo(
    () => getNumOfRequiredEntriesInSection(section),
    [section]
  )
  const numOfCompletedRequiredEntries = useMemo(
    () => getNumOfCompletedRequiredEntriesInSection(section),
    [section]
  )
  const requiredText =
    numOfRequiredEntries !== 0
      ? `${numOfCompletedRequiredEntries}/${numOfRequiredEntries} required`
      : ''
  useEffect(() => {
    if (numOfTotalEntries !== numOfCompletedEntries) {
      setExpanded(true)
    } else {
      setExpanded(false)
    }
  }, [numOfCompletedEntries, numOfTotalEntries])
  const filteredEntries = useMemo(
    () =>
      showIncomplete
        ? section.entries.filter(
            (entry) => !checkSingleEntryIsCompleteOrNotRequired(entry)
          )
        : section.entries,
    [section.entries, showIncomplete]
  )
  if (filteredEntries.length === 0) {
    return <></>
  }
  return (
    <>
      <Accordion
        {...accordionProps}
        sx={{ mb: 1 }}
        disableGutters
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: 'primary.light'
          }}>
          <Stack sx={{ width: '100%' }}>
            <Stack direction="row" justifyContent={'space-between'} spacing={1}>
              <Typography variant="subtitle1">
                {section.info!.title!}
              </Typography>
              <LinearProgressWithLabel
                variant="determinate"
                {...{
                  numOfCompletedEntries,
                  numOfTotalEntries
                }}
              />
            </Stack>
            <Stack direction="row" justifyContent={'space-between'} spacing={1}>
              <Typography variant="body1">
                {section.info!.description}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {requiredText}
              </Typography>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0, overflowX: 'scroll', width: '100%' }}>
          <Stack spacing={1}>
            {filteredEntries.map((entry) =>
              isCached ? (
                <SinglePromptOfEntryCached key={entry.id!} entry={entry} />
              ) : (
                <SinglePromptOfEntry key={entry.id!} entry={entry} />
              )
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </>
  )
}

export default SingleSection
